/** Integer 2D point used for area coordinates and object IDs. Immutable. */
export class KnyttPoint {
  constructor(
    public readonly x: number,
    public readonly y: number,
  ) {}

  static readonly Zero = new KnyttPoint(0, 0);

  /** Map key — JS Maps use reference equality for objects, so we key by string. */
  get key(): string {
    return `${this.x},${this.y}`;
  }

  static fromKey(key: string): KnyttPoint {
    const [x, y] = key.split(',').map(Number);
    return new KnyttPoint(x, y);
  }

  get isZero(): boolean {
    return this.x === 0 && this.y === 0;
  }

  add(p: KnyttPoint): KnyttPoint {
    return new KnyttPoint(this.x + p.x, this.y + p.y);
  }

  sub(p: KnyttPoint): KnyttPoint {
    return new KnyttPoint(this.x - p.x, this.y - p.y);
  }

  min(p: KnyttPoint): KnyttPoint {
    return new KnyttPoint(Math.min(this.x, p.x), Math.min(this.y, p.y));
  }

  max(p: KnyttPoint): KnyttPoint {
    return new KnyttPoint(Math.max(this.x, p.x), Math.max(this.y, p.y));
  }

  manhattanDistance(p: KnyttPoint): number {
    return Math.abs(p.x - this.x) + Math.abs(p.y - this.y);
  }

  equals(p: KnyttPoint | null | undefined): boolean {
    return !!p && p.x === this.x && p.y === this.y;
  }

  toString(): string {
    return `(${this.x}, ${this.y})`;
  }
}
